import { IOError } from '@eversports/io-error'
import { isTinValidationRequired, preValidateTin } from '@eversports/tin/tin-limits'
import isEmail from 'validator/lib/isEmail'
import isFQDN from 'validator/lib/isFQDN'
import isURL from 'validator/lib/isURL'
import xss from 'xss'

import type { MarkValidatorFn, ValidatorFn } from './types'

// eslint-disable-next-line @typescript-eslint/no-unsafe-return
export const validator: MarkValidatorFn = (fn) => fn as any

export function email(): ValidatorFn<string> {
  const error = IOError.factory('invalid-email')
  return (input) => {
    if (!isEmail(input)) {
      throw error(input)
    }
    return input
  }
}

export function domain(): ValidatorFn<string> {
  const error = IOError.factory('invalid-domain')
  return (input) => {
    // Empty domains are allowed (optional field)
    if (!input || input.trim() === '') {
      return input
    }

    const trimmedInput = input.trim()

    // Check if input starts with @ (common mistake for Instagram handles)
    if (trimmedInput.startsWith('@')) {
      throw error(input)
    }

    // Check if it's a valid URL or domain
    const isValidURL = isURL(trimmedInput, {
      require_protocol: false,
      require_valid_protocol: true,
      protocols: ['http', 'https'],
      require_tld: true,
    })

    const isValidDomain = isFQDN(trimmedInput, {
      require_tld: true,
      allow_underscores: false,
      allow_trailing_dot: false,
    })

    // Must be either a valid URL or a valid domain
    if (!isValidURL && !isValidDomain) {
      throw error(input)
    }

    return input
  }
}

export function password(): ValidatorFn<string> {
  const error = IOError.factory('invalid-password')
  return (input) => {
    if (input.length < 12) {
      throw error(input)
    }
    return input
  }
}

export function required(): ValidatorFn<string> {
  const error = IOError.factory('required')
  return (input) => {
    if (!input || (typeof input === 'string' && input.trim() === '')) {
      throw error(input)
    }
    return input
  }
}

interface PhoneOptions {
  withoutCountryCode?: boolean
}
export function telephone({ withoutCountryCode = false }: PhoneOptions = {}): ValidatorFn<string> {
  const error = IOError.factory('invalid-phone')
  return (input) => {
    // NOTE(swatinem): this is copied from `utilities`, to avoid importing the
    // the whole thing. The check itself would need a lot of improvement anyway
    if (withoutCountryCode && !input.match(/^[0-9 /()-]{3,30}$/)) {
      throw error(input)
    }
    if (!withoutCountryCode && !input.match(/^\+?[0-9 /()-]{5,30}$/)) {
      throw error(input)
    }
    return input
  }
}

interface FiscalCodeOptions {
  amount?: {
    value: number
    currency: string
  }
  countryCode: string
  customerCountryCode?: string
}

export function fiscalCode(_codeOptions: FiscalCodeOptions): ValidatorFn<string> {
  const { countryCode, customerCountryCode, amount } = _codeOptions
  const error = IOError.factory('invalid-fiscal-code')

  return (input) => {
    // is tin required should always be checked with the country code of the venue
    if (!isTinValidationRequired({ countryCode, amount, isFractionalUnitRequired: true })) {
      return input
    }

    if (customerCountryCode && customerCountryCode.trim().toUpperCase() !== countryCode.trim().toUpperCase()) {
      // if the customer country code is different from the venue country code, we skip the validation
      return input
    }

    const isTinValid = preValidateTin(countryCode, input)

    if (!isTinValid) {
      throw error(input)
    }

    return input
  }
}

interface LengthOptions {
  min?: number
  max?: number
}
export function length({ min = 0, max = Infinity }: LengthOptions): ValidatorFn<string> {
  const options = { min, max }
  const minError = IOError.factory('invalid-min-length', options)
  const maxError = IOError.factory('invalid-max-length', options)
  return (input) => {
    // eslint-disable-next-line no-shadow
    const { length } = input
    if (length < min) {
      throw minError(input)
    }
    if (length > max) {
      throw maxError(input)
    }
    return input
  }
}

interface NumberOptions {
  min?: number
  max?: number
}
export function number({ min = 0, max = Infinity }: NumberOptions): ValidatorFn<number> {
  const options = { min, max }
  const minError = IOError.factory('invalid-min-value', options)
  const maxError = IOError.factory('invalid-max-value', options)
  return (input) => {
    if (input < min) {
      throw minError(input)
    }
    if (input > max) {
      throw maxError(input)
    }
    return input
  }
}

interface ArrayOptions<T> {
  element?: ValidatorFn<T>
  min?: number
  max?: number
}
export function array<T = any>({ element = (e) => e, min = 0, max = Infinity }: ArrayOptions<T> = {}): ValidatorFn<
  Array<T>
> {
  const options = { min, max }
  const minError = IOError.factory('invalid-min-array-length', options)
  const maxError = IOError.factory('invalid-max-array-length', options)
  const validatorFn: ValidatorFn<Array<T>> = (input) => {
    // eslint-disable-next-line no-shadow
    const { length } = input
    if (length < min) {
      throw minError(input)
    }
    if (length > max) {
      throw maxError(input)
    }
    return input
  }
  validatorFn.validator = element

  return validatorFn
}

export function hasXssCharacters(text: string) {
  const result = xss(text, { stripIgnoreTag: true })
  return result !== text
}

export function xssCheck(): ValidatorFn<string> {
  const xssError = IOError.factory('invalid-character')
  return (input) => {
    if (hasXssCharacters(input)) {
      throw xssError()
    }
    return input
  }
}
